import "./App.css";

import { useEffect, useState } from "react";

import { Link } from "react-router-dom";
import { WEB_APP_URL } from "./config";

/** Mirrors fit-match/Services/stripe.ts SUBSCRIPTION_PLANS (keep in sync). */
const MAP_ONLY_PLAN_FEATURES = [
  "Display your profile on the map",
  "Message clients directly",
  "Custom profile page with photos and videos",
  "Receive reviews from clients",
  "Manage and display your services",
  "View client profiles",
  "Connect with clients",
];

const FULL_MEMBERSHIP_PLAN_FEATURES = [
  "All features of the Map Only plan",
  "Accept unlimited client bookings",
  "Custom availability settings",
  "Manage bookings and calendar",
  "Payment processing setup (Stripe Connect)",
  "Receive payouts from bookings",
  "Booking request notifications",
  "View booking history and analytics",
];

function ForProfessionals() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [hasLaunched, setHasLaunched] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const targetDate = new Date("2026-01-01T00:00:00").getTime();
      const difference = targetDate - now;

      if (difference > 0) {
        const days = Math.floor(difference / (1000 * 60 * 60 * 24));
        const hours = Math.floor(
          (difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
        );
        const minutes = Math.floor(
          (difference % (1000 * 60 * 60)) / (1000 * 60)
        );
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);

        setTimeLeft({ days, hours, minutes, seconds });
        setHasLaunched(false);
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        setHasLaunched(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bg-black text-gray-200">
      {/* Navbar */}
      <header className="bg-black bg-opacity-70 backdrop-blur-lg sticky top-0 w-full z-50 shadow-lg">
        <nav className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <img
              src="/images/logo.png"
              alt="Fit-Match Logo"
              className="h-16 w-auto"
            />
            <Link to="/" className="text-2xl font-bold gradient-text">
              FIT-MATCH
            </Link>
          </div>
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/#how-it-works" className="nav-link font-medium">
              How It Works
            </Link>
            <Link to="/our-story" className="nav-link font-medium">
              Our Story
            </Link>
            <Link to="/professionals" className="nav-link font-medium">
              Pros
            </Link>
            <Link to="/training-providers" className="nav-link font-medium">
              Providers
            </Link>
            <Link to="/communities" className="nav-link font-medium">
              Communities
            </Link>
            <Link to="/facilities" className="nav-link font-medium">
              Facilities
            </Link>
            <a
              href={WEB_APP_URL}
              className="nav-link font-medium px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 text-white"
            >
              Login
            </a>
          </div>
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="text-white focus:outline-none"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M4 6h16M4 12h16m-4 6h4"
                ></path>
              </svg>
            </button>
          </div>
        </nav>
        {/* Mobile Menu */}
        <div
          className={`${
            isMobileMenuOpen ? "block" : "hidden"
          } md:hidden bg-black bg-opacity-90 py-4`}
        >
          <Link
            to="/#how-it-works"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            How It Works
          </Link>
          <Link
            to="/our-story"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Our Story
          </Link>
          <Link
            to="/professionals"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Pros
          </Link>
          <Link
            to="/training-providers"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Providers
          </Link>
          <Link
            to="/communities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Communities
          </Link>
          <Link
            to="/facilities"
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Facilities
          </Link>
          <a
            href={WEB_APP_URL}
            className="block px-6 py-2 text-white font-medium nav-link"
          >
            Login
          </a>
        </div>
      </header>

      <main className="pt-24">
        {/* Hero Section */}
        <section className="hero-bg-pro py-12 md:py-20 text-center relative overflow-hidden">
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
          <div className="container mx-auto px-6 relative z-10">
            <h1 className="text-4xl md:text-6xl font-extrabold leading-tight tracking-tight">
              Grow Your Business.{" "}
              <span className="gradient-text">Find Your Clients.</span>
            </h1>
            <p className="mt-4 text-lg md:text-xl max-w-2xl mx-auto">
              The platform designed to eliminate no-shows, streamline bookings,
              and get you paid faster.
            </p>

            {/* Countdown Timer or Download Button */}
            {hasLaunched ? (
              <div className="mt-8 mb-8">
                <div className="bg-green-600 bg-opacity-20 border-2 border-green-500 rounded-lg p-6 md:p-8">
                  <h3 className="text-2xl md:text-3xl font-bold mb-4 text-green-400">
                    🎉 Fit-Match is Now Live! 🎉
                  </h3>
                  <p className="text-lg md:text-xl text-white mb-6">
                    Use Fit-Match on the web or download the app for the full
                    experience.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                    <a
                      href={WEB_APP_URL}
                      className="inline-block bg-green-600 text-white font-bold py-4 px-8 rounded-full shadow-lg hover:bg-green-700 transition duration-300 w-full sm:w-auto text-center"
                    >
                      Open web app
                    </a>
                    <a
                      href="https://apps.apple.com/gb/app/fit-match/id6753618529"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-block bg-blue-fit-match text-white font-bold py-4 px-8 rounded-full shadow-lg hover:bg-blue-600 transition duration-300 w-full sm:w-auto text-center"
                    >
                      Download on App Store
                    </a>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="mt-8 mb-8">
                  <h3 className="text-xl md:text-2xl font-semibold mb-4 text-white">
                    Launch Countdown
                  </h3>
                  <div className="flex justify-center items-center space-x-4 md:space-x-8">
                    <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                      <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                        {timeLeft.days}
                      </div>
                      <div className="text-sm md:text-base text-gray-300">Days</div>
                    </div>
                    <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                      <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                        {timeLeft.hours}
                      </div>
                      <div className="text-sm md:text-base text-gray-300">
                        Hours
                      </div>
                    </div>
                    <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                      <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                        {timeLeft.minutes}
                      </div>
                      <div className="text-sm md:text-base text-gray-300">
                        Minutes
                      </div>
                    </div>
                    <div className="bg-gray-800 bg-opacity-80 rounded-lg p-4 md:p-6 text-center min-w-[80px] md:min-w-[100px]">
                      <div className="text-2xl md:text-4xl font-bold text-blue-fit-match">
                        {timeLeft.seconds}
                      </div>
                      <div className="text-sm md:text-base text-gray-300">
                        Seconds
                      </div>
                    </div>
                  </div>
                  <p className="text-sm md:text-base text-gray-300 mt-4">
                    Until January 1st, 2026
                  </p>
                  <div className="mt-4 p-4 bg-gray-700 bg-opacity-50 border border-gray-600 border-opacity-50 rounded-lg">
                    <p className="text-white font-semibold text-sm md:text-base">
                      Pre-registration is now closed. The app will be available on January 1st, 2026.
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-20 bg-gray-950">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              Features Designed for Your Success
            </h2>
            <p className="text-gray-400 text-center max-w-2xl mx-auto mb-12 text-sm md:text-base">
              Map Only and Full Membership share core discovery and messaging.
              Full Membership adds bookings, availability, Stripe Connect, and
              payouts.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  Map Only and Full
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Map discovery
                </h3>
                <p className="text-gray-400 text-center">
                  With an active membership, services, and location, clients find
                  you on the map and open your profile.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  Map Only and Full
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Profile and reviews
                </h3>
                <p className="text-gray-400 text-center">
                  Custom profile with photos and videos, plus client reviews to
                  build trust.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  Map Only and Full
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Client messaging
                </h3>
                <p className="text-gray-400 text-center">
                  Message clients directly, view client profiles, and stay
                  connected in one place.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300 ring-1 ring-green-500/40">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-amber-300 mb-3">
                  Full Membership
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M8 7V3m8 4V3m-9 8h.01M3 21h18a2 2 0 002-2V8.656a2 2 0 00-.606-1.424l-3.342-3.342A2 2 0 0013.586 3H10.414A2 2 0 009 5.828v2.172a2 2 0 00-.606 1.424l-3.342 3.342A2 2 0 003 16.344V21z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Bookings, availability, and payments
                </h3>
                <p className="text-gray-400 text-center">
                  Unlimited client bookings, custom availability, in-app calendar,
                  Stripe Connect setup, payouts, and booking notifications.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  All pros · Full for booking analytics
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0a2 2 0 002-2h2a2 2 0 002-2v-2"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Analytics and history
                </h3>
                <p className="text-gray-400 text-center">
                  Profile analytics in the app for every professional. Booking
                  history and deeper booking analytics with Full Membership.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  Map Only and Full
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Refer other professionals
                </h3>
                <p className="text-gray-400 text-center">
                  Share your referral link with other pros. Rewards apply when
                  invitees complete signup and pay for membership.
                </p>
              </div>
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300">
                <p className="text-center text-xs font-semibold uppercase tracking-wide text-green-400 mb-3">
                  Map Only and Full
                </p>
                <div className="text-blue-fit-match text-4xl mb-4">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-12 w-12 mx-auto"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </div>
                <h3 className="text-xl font-bold mb-2 text-center">
                  Fit-Match ID
                </h3>
                <p className="text-gray-400 text-center">
                  Shareable professional profile link and QR-style presence so
                  clients can find you quickly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Screenshots Section */}
        <section id="screenshots" className="py-20 bg-black">
          <div className="container mx-auto px-6 text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-12">
              See the Professional Dashboard in Action
            </h2>
            <div className="flex flex-wrap justify-center items-center gap-12">
              <div className="app-screenshot-frame -rotate-3 transform transition duration-300 hover:rotate-0">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage: "url('/images/screenshots/pro-home.png')",
                  }}
                ></div>
              </div>
              <div className="app-screenshot-frame rotate-6 transform transition duration-300 hover:rotate-0">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage:
                      "url('/images/screenshots/my-clients.png')",
                  }}
                ></div>
              </div>
              <div className="app-screenshot-frame -rotate-6 transform transition duration-300 hover:rotate-0">
                <div
                  className="app-screenshot-inner"
                  style={{
                    backgroundImage:
                      "url('/images/screenshots/fit-match-id.png')",
                  }}
                ></div>
              </div>
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="py-20 bg-gray-950">
          <div className="container mx-auto px-6 text-center max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-gray-400 leading-relaxed mb-8">
              Join a community of trusted professionals and take control of your
              bookings, payments, and client base. It's time to focus on what
              you do best—training.
            </p>
            {hasLaunched ? (
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a
                  href={WEB_APP_URL}
                  className="bg-green-600 text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-green-700 transition duration-300 inline-block w-full sm:w-auto text-center"
                >
                  Open web app
                </a>
                <a
                  href="https://apps.apple.com/gb/app/fit-match/id6753618529"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-fit-match text-white font-bold py-3 px-8 rounded-full shadow-lg hover:bg-blue-600 transition duration-300 inline-block w-full sm:w-auto text-center"
                >
                  Download on App Store
                </a>
              </div>
            ) : (
              <p className="text-gray-300 font-semibold">
                Pre-registration is now closed. The app will be available on January 1st, 2026.
              </p>
            )}
          </div>
        </section>

        {/* Pricing Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-6">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
              Choose Your Plan
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {/* Map Only plan */}
              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300 border border-gray-700">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2 text-blue-fit-match">
                    Map Only
                  </h3>
                  <div className="text-3xl font-bold text-white mb-2">
                    £9.99
                  </div>
                  <div className="text-gray-400 text-sm">per month</div>
                </div>
                <div className="mb-2">
                  <h4 className="text-lg font-semibold mb-3 text-white">
                    What&apos;s included
                  </h4>
                  <ul className="text-gray-400 text-sm leading-relaxed space-y-2">
                    {MAP_ONLY_PLAN_FEATURES.map((line) => (
                      <li key={line} className="flex items-start">
                        <span className="text-blue-fit-match mr-2 shrink-0">
                          •
                        </span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-gray-800 p-8 rounded-xl shadow-xl transform hover:scale-105 transition duration-300 border border-gray-700 border-green-500/30">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold mb-2 text-blue-fit-match">
                    Full Membership
                  </h3>
                  <div className="text-3xl font-bold text-white mb-2">
                    £19.99
                  </div>
                  <div className="text-gray-400 text-sm">per month</div>
                </div>
                <div className="mb-2">
                  <h4 className="text-lg font-semibold mb-3 text-white">
                    What&apos;s included
                  </h4>
                  <ul className="text-gray-400 text-sm leading-relaxed space-y-2">
                    {FULL_MEMBERSHIP_PLAN_FEATURES.map((line) => (
                      <li key={line} className="flex items-start">
                        <span className="text-blue-fit-match mr-2 shrink-0">
                          •
                        </span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; {new Date().getFullYear()} Fit-Match. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default ForProfessionals;
